'use client';

import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { usePrivy } from '@privy-io/react-auth';
import twitterLogo from '@/public/twitter.svg';
import { UniversalAvatar } from '@/app/components/UniversalAvatar';
import { useAtomValue } from 'jotai';
import { userDataAtom } from '@/app/atoms/userData';
import React, { useState } from 'react';
import HeaderDelegationModal from '@/app/components/Modals/HeaderDelegationModal';
import CardDelegationModal from '@/app/components/Modals/CardDelegationModal';
import { MergedDelegateInfo } from '@/app/atoms/combinedDataAtom';
import { Address } from 'viem';

interface DelegateProps {
  delegate: MergedDelegateInfo;
}

const ProfileContent = ({ delegate }: DelegateProps) => {
  const userData = useAtomValue(userDataAtom);
  const pathname = usePathname();
  const [delegateModalIsOpen, setDelegateModalIsOpen] = useState(false);
  const [CardDelegationModalIsOpen, setCardDelegationModalIsOpen] = useState(false);
  const [activeModalDelegate, setActiveModalDelegate] = useState<`0x${string}`>('0x');
  const [activeAnotherModalDelegate, setActiveAnotherModalDelegate] = useState<
    `0x${string}` | null
  >(null);
  const [action, setAction] = useState<'delegate' | 'revoke'>('delegate');
  const delegatesAddress = pathname.split('/')[4] as `0x${string}`;
  const { user } = usePrivy();
  const address = user?.wallet?.address as `0x${string}`;
  const tokenBalance = delegate?.tokenBalance?.toString();
  const isUsersDelegate = delegate.delegators?.includes(address as Address);
  const selfDelegatedOnly =
    delegate.delegationsReceived === 1 && delegate.isUserSelfDelegated;

  const usersProfile = address === delegatesAddress;

  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = async () => {
    if (delegate.address) {
      try {
        await navigator.clipboard.writeText(delegate.address);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      } catch (err) {
        setCopySuccess(false);
      }
    }
  };

  const handleClick = (action: React.SetStateAction<'delegate' | 'revoke'>) => {
    if (usersProfile) {
      setDelegateModalIsOpen(true);
    } else {
      setCardDelegationModalIsOpen(true);
      setAction(action);
    }
  };

  return (
    <div className="flex flex-col p-4 border border-neutral-700 rounded-lg xs:shadow-md gap-4 mt-4">
      <div className="flex items-center justify-between space-x-4 w-full">
        <div className="flex relative items-center flex-1">
          <UniversalAvatar
            address={delegatesAddress || ''}
            width={52}
            height={50}
            size={40}
            tokenBalance={tokenBalance}
            showTokenBalance={true}
          />
          <div className="absolute bottom-1 left-48">
            {delegate?.twitter_handle && (
              <Link
                href={delegate?.twitter_link || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="max-narrow:hidden hover:bg-neutral-100 rounded-full p-2"
                title={delegate?.twitter_handle || ''}
              >
                <Image
                  src={twitterLogo}
                  alt="Twitter Logo"
                  width={20}
                  height={20}
                  style={{ width: '20px', height: '20px' }}
                  className={delegate?.twitter_handle ? '' : 'cursor-not-allowed'}
                />
              </Link>
            )}
          </div>
        </div>
        <div className="flex items-center">
          {usersProfile && (
            <Link
              href={`/karratco/view/settings`}
              className={`px-4 font-bold text-neutral-50bg-link narrow:ml-auto rounded-md hover:bg-hover flex items-center max-h-[20px]`}
            >
              Edit
            </Link>
          )}
        </div>
      </div>
      {delegate?.bio && (
        <>
          <div className="flex justify-start font-bold mt-2">Bio</div>
          <div className="flex justify-start">{delegate?.bio}</div>
        </>
      )}
      {delegate?.delegation_statement && (
        <>
          <div className="flex justify-start font-bold mt-2">Delegation Statement</div>
          <div className="flex justify-start">{delegate?.delegation_statement}</div>
        </>
      )}

      <div className="flex max-narrow:flex-col-reverse mt-4">
        {selfDelegatedOnly ? (
          <div className="flex items-center mt-2 narrow:mt-0"> Self Delegated</div>
        ) : (
          <div className="flex items-center mt-2 narrow:mt-0">
            <span>{delegate?.delegationsReceived || '0'} Delegations</span>
          </div>
        )}
        {!isUsersDelegate ? (
          <button
            onClick={() => handleClick('delegate')}
            className={`px-4 py-2 font-bold text-neutral-50 border border-white bg-transparent narrow:ml-auto rounded hover:bg-hover hover:border-transparent hover:cursor-pointer`}
          >
            Delegate
          </button>
        ) : (
          <button
            onClick={() => handleClick('revoke')}
            className={`px-4 py-2 font-bold text-neutral-50bg-link narrow:ml-auto rounded hover:bg-hover`}
          >
            Revoke
          </button>
        )}
      </div>

      {delegateModalIsOpen && (
        <HeaderDelegationModal
          isOpen={!!delegateModalIsOpen}
          onClose={() => {
            setActiveModalDelegate('0x');
            setDelegateModalIsOpen(false);
          }}
          userData={userData}
          activeModalDelegate={activeModalDelegate}
          setActiveModalDelegate={setActiveModalDelegate}
        />
      )}
      {CardDelegationModalIsOpen && (
        <CardDelegationModal
          isOpen={!!CardDelegationModalIsOpen}
          onClose={() => {
            setActiveModalDelegate('0x');
            setCardDelegationModalIsOpen(false);
          }}
          action={'delegate'}
          delegateAddress={delegate.address}
          activeModalDelegate={activeAnotherModalDelegate}
          setActiveModalDelegate={setActiveAnotherModalDelegate}
        />
      )}
    </div>
  );
};

export default ProfileContent;
